import {
  Button,
  Icon,
  Modal,
  PageHeader,
  Table,
  TableColumn,
} from '@components';
import CostcenterFromToApiCaller from '@services/api/costCenterFromTo';
import CostcenterApiCaller from '@services/api/costCenter';
import { CostCenterFromTo } from 'interfaces/CostCenterFromTo';
import { useCallback, useEffect, useState } from 'react';
import CostCenterFromToForm from './Form/CostCenterFromToForm';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import { CostCenterFromToFilters } from '@services/api/costCenterFromTo/calls';
import { usePermissionContext } from '@contexts/PermissionProvider';

export default function CostCenterFromToPage() {
  const [tableData, setTableData] = useState<{
    result: CostCenterFromTo[];
    total: number;
  }>({
    result: [],
    total: 0,
  });
  const [filters, setFilters] = useState<CostCenterFromToFilters>({});
  const [isEditModalOpen, toggleEditModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId] = useState('');
  const confirmation = useConfirmationModal();

  const { hasPermission } = usePermissionContext();

  useEffect(() => {
    loadTableData(filters);
  }, [filters]);

  const loadCostCenterFilters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostcenterApiCaller.listAll().then((values) => {
        values.result.unshift({
          id: 'clearOptions',
          name: 'TODOS',
        });
      });
    }
  }, [hasPermission]);

  useEffect(() => {
    loadCostCenterFilters();
  }, [loadCostCenterFilters]);

  const loadTableData = (filters: CostCenterFromToFilters) => {
    setIsLoading(true);
    CostcenterFromToApiCaller.list(filters)
      .then((values) => setTableData(values))
      .finally(() => setIsLoading(false));
  };

  const handleEditModal = (editId: string, open: boolean) => {
    setEditId(editId);
    toggleEditModal(open);
  };

  const handleRemoveCostCenter = async (removeId: string) => {
    confirmation({
      title: 'Excluir centro de custo',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir esse centro de custo?',
      onSubmit: () =>
        CostcenterFromToApiCaller.remove(removeId).then((_) =>
          loadTableData(filters)
        ),
    });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Centro de custo de/para" />

        <div className="mt-s-200">
          <Table
            data={tableData.result}
            totalRecords={tableData.total}
            dataKey="from"
            showColumnSelector={false}
            withPagination
            filters={filters}
            setFilters={setFilters}
            isLoading={isLoading}
            withSearchInput
            additionalFilters={
              <div className="d-flex justify-end">
                <div className="row col-sm-12 d-flex justify-end">
                  <Button
                    className="ml-s-100"
                    suffixes={<Icon>add</Icon>}
                    design="filled"
                    onClick={() => handleEditModal('', true)}
                  >
                    Novo de/para
                  </Button>
                </div>
              </div>
            }
          >
            <TableColumn fromKey="from" header="Centro de custo de" />
            <TableColumn
              header="Centro de custo para"
              fromKey="to"
              render={(to) => to.name}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => {
                      handleEditModal(id, true);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    onClick={async () => handleRemoveCostCenter(id)}
                  />
                );
              }}
            />
          </Table>
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClickAway={() => {
          setEditId('');
          toggleEditModal(false);
        }}
        size="extra-small"
        title={editId === '' ? 'Cadastrar De/Para' : 'Editar De/Para'}
      >
        <CostCenterFromToForm
          editId={editId}
          onSave={() => loadTableData(filters)}
          closeModal={() => handleEditModal('', false)}
        ></CostCenterFromToForm>
      </Modal>
    </>
  );
}
